import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

// Per-provider setup guides — step-by-step instructions a non-technical
// reseller can follow. Mirrors the SOCIAL_SETUP_GUIDES pattern in
// tournament-web/src/pages/settings/Settings.jsx.
// Each translatable string carries a `key` (its i18n tag) alongside the
// English source. EmailSetupGuideModal resolves these with t(key, english)
// at render time so the active language wins while English stays the fallback.
const EMAIL_SETUP_GUIDES = {
  sendgrid: {
    titleKey: 'sp_sendgrid_guide_title',
    title: 'Get your SendGrid API key',
    estimatedMinutes: 8,
    notes: [
      { key: 'sp_sendgrid_note_free_tier', text: "SendGrid's free tier sends up to 100 emails/day — plenty for getting started." },
      { key: 'sp_sendgrid_note_verify_sender', text: 'You must verify either a Single Sender (one email address) or your full domain before sending.' },
    ],
    steps: [
      { titleKey: 'sp_sendgrid_step_signup_title', title: 'Sign up for SendGrid', bodyKey: 'sp_sendgrid_step_signup_body', body: 'Create a free account at sendgrid.com. Confirm your email address.', link: { labelKey: 'sp_link_sendgrid_com', label: 'sendgrid.com', url: 'https://signup.sendgrid.com/' } },
      { titleKey: 'sp_sendgrid_step_verify_title', title: 'Verify your sender', bodyKey: 'sp_sendgrid_step_verify_body', body: 'Settings → Sender Authentication. Pick "Single Sender Verification" (fastest) or "Domain Authentication" (better deliverability). Verify the email/domain you plan to send FROM.', link: { labelKey: 'sp_link_sender_auth', label: 'Sender Auth', url: 'https://app.sendgrid.com/settings/sender_auth' } },
      { titleKey: 'sp_sendgrid_step_apikey_title', title: 'Create an API Key', bodyKey: 'sp_sendgrid_step_apikey_body', body: 'Settings → API Keys → Create API Key. Name it "InteGolf Reseller". Choose "Restricted Access" and grant "Mail Send" permission only.', link: { labelKey: 'sp_link_api_keys', label: 'API Keys', url: 'https://app.sendgrid.com/settings/api_keys' } },
      { titleKey: 'sp_sendgrid_step_copy_title', title: 'Copy the key (shown only once)', bodyKey: 'sp_sendgrid_step_copy_body', body: 'SendGrid shows the key starting with SG.… on the next screen. Copy it now — you cannot see it again. If lost, create a new key.' },
      { titleKey: 'sp_guide_moneris_step_paste_title', title: 'Paste into InteGolf', bodyKey: 'sp_sendgrid_step_paste_body', body: 'Paste the API key into the "SendGrid API key" field. Set the From email to the sender you verified in step 2. Click Save.' },
    ],
  },
  aws_ses: {
    titleKey: 'sp_ses_guide_title',
    title: 'Set up Amazon SES',
    estimatedMinutes: 25,
    notes: [
      { key: 'sp_ses_note_aws_account', text: 'You need an AWS account (sign up at aws.amazon.com if you do not have one — credit card required even for free tier).' },
      { key: 'sp_ses_note_sandbox', text: 'New SES accounts start in "sandbox" mode — recipients must also be verified. Request production access (24-hour turnaround) before going live.' },
      { key: 'sp_ses_note_region', text: 'SES is region-specific. Pick one region (e.g. us-east-1) and stick with it.' },
    ],
    steps: [
      { titleKey: 'sp_ses_step_signin_title', title: 'Sign in to AWS Console', bodyKey: 'sp_ses_step_signin_body', body: 'Use your AWS root account or an IAM user with admin rights for this setup.', link: { labelKey: 'sp_link_aws_console', label: 'AWS Console', url: 'https://console.aws.amazon.com/' } },
      { titleKey: 'sp_ses_step_region_title', title: 'Pick a region', bodyKey: 'sp_ses_step_region_body', body: 'Top-right of the console. Common choices: us-east-1 (Virginia), us-west-2 (Oregon), eu-west-1 (Ireland). Whichever you pick, use the SAME region for everything below.' },
      { titleKey: 'sp_ses_step_verify_title', title: 'Verify a sender identity', bodyKey: 'sp_ses_step_verify_body', body: 'Search for "SES" → Verified identities → Create identity. Pick "Email address" (fastest) or "Domain" (recommended for production). Follow the verification steps.', link: { labelKey: 'sp_link_ses_verified_identities', label: 'SES Verified Identities', url: 'https://console.aws.amazon.com/ses/home#/verified-identities' } },
      { titleKey: 'sp_ses_step_prod_title', title: 'Request production access', bodyKey: 'sp_ses_step_prod_body', body: 'In SES → Account dashboard → "Request production access". Without this, you can only send to verified addresses. Approval is usually 24 hours.' },
      { titleKey: 'sp_ses_step_iam_title', title: 'Create an IAM user with SES send permission', bodyKey: 'sp_ses_step_iam_body', body: 'IAM → Users → Create user → name it "integolf-ses-sender". Attach policy "AmazonSESFullAccess" (or a custom policy with just ses:SendEmail and ses:SendRawEmail).', link: { labelKey: 'sp_link_iam_users', label: 'IAM Users', url: 'https://console.aws.amazon.com/iam/home#/users' } },
      { titleKey: 'sp_ses_step_keys_title', title: 'Generate Access Keys', bodyKey: 'sp_ses_step_keys_body', body: 'On the IAM user → Security credentials tab → Create access key → "Application running outside AWS". Copy the Access Key ID AND the Secret Access Key NOW — the secret cannot be retrieved later.' },
      { titleKey: 'sp_guide_moneris_step_paste_title', title: 'Paste into InteGolf', bodyKey: 'sp_ses_step_paste_body', body: 'Access Key ID + Secret Access Key + Region (the one you picked in step 2). From email = the address/domain you verified.' },
    ],
  },
  gmail_oauth: {
    titleKey: 'sp_gmail_oauth_guide_title',
    title: 'Connect with Google (OAuth)',
    estimatedMinutes: 2,
    notes: [
      { key: 'sp_gmail_oauth_note_easiest', text: "Easiest option — works with any Gmail account (personal @gmail.com or Google Workspace)." },
      { key: 'sp_gmail_oauth_note_send_only', text: 'Requires no credentials from you. We only request "send" permission — we cannot read your inbox.' },
    ],
    steps: [
      { titleKey: 'sp_gmail_oauth_step_click_title', title: 'Click "Connect with Google"', bodyKey: 'sp_gmail_oauth_step_click_body', body: 'Below this guide, click the green "Connect with Google" button. A new window opens to Google.' },
      { titleKey: 'sp_gmail_oauth_step_signin_title', title: 'Sign in to your Google account', bodyKey: 'sp_gmail_oauth_step_signin_body', body: 'Use the Google account you want emails to be sent FROM (customer confirmations, review invites, etc.).' },
      { titleKey: 'sp_gmail_oauth_step_grant_title', title: 'Grant send permission', bodyKey: 'sp_gmail_oauth_step_grant_body', body: 'Google shows a consent screen listing "Send email on your behalf". Click Allow. You\'ll be redirected back to InteGolf.' },
      { titleKey: 'sp_guide_step_done_title', title: 'Done', bodyKey: 'sp_gmail_oauth_step_done_body', body: 'Your account appears in the integration list as Gmail. Sent mail will show up in your Sent folder, branded with your account.' },
    ],
  },
  gmail_app_password: {
    titleKey: 'sp_gmail_apppw_guide_title',
    title: 'Use a Gmail app password',
    estimatedMinutes: 5,
    notes: [
      { key: 'sp_gmail_apppw_note_2step', text: 'Requires 2-Step Verification enabled on your Google account.' },
      { key: 'sp_gmail_apppw_note_works_for', text: 'Works for personal @gmail.com OR Google Workspace addresses.' },
      { key: 'sp_gmail_apppw_note_revokable', text: 'App passwords bypass 2FA only for the app you create them for; revokable any time.' },
    ],
    steps: [
      { titleKey: 'sp_gmail_apppw_step_2step_title', title: 'Enable 2-Step Verification', bodyKey: 'sp_gmail_apppw_step_2step_body', body: 'If you have not already: myaccount.google.com → Security → 2-Step Verification → Turn on. Use SMS or an authenticator app.', link: { labelKey: 'sp_link_2step_setup', label: '2-Step setup', url: 'https://myaccount.google.com/signinoptions/two-step-verification' } },
      { titleKey: 'sp_gmail_apppw_step_open_title', title: 'Open App Passwords', bodyKey: 'sp_gmail_apppw_step_open_body', body: 'After 2-Step is on, go to myaccount.google.com/apppasswords. (If the page says "this setting is not available" you do not have 2FA enabled — go back to step 1.)', link: { labelKey: 'sp_link_app_passwords', label: 'App Passwords', url: 'https://myaccount.google.com/apppasswords' } },
      { titleKey: 'sp_gmail_apppw_step_create_title', title: 'Create a new app password', bodyKey: 'sp_gmail_apppw_step_create_body', body: 'In the "App name" field, type "InteGolf" and click Create. Google shows a 16-character password in a yellow box.' },
      { titleKey: 'sp_gmail_apppw_step_copy_title', title: 'Copy the password (no spaces)', bodyKey: 'sp_gmail_apppw_step_copy_body', body: 'Copy the 16-character string. Strip any spaces — Google sometimes shows them grouped. Treat it like a password.' },
      { titleKey: 'sp_guide_moneris_step_paste_title', title: 'Paste into InteGolf', bodyKey: 'sp_gmail_apppw_step_paste_body', body: 'Your full Gmail address into the email field. The 16-char app password into the App password field. Click Save integration.' },
    ],
  },
  microsoft_oauth: {
    titleKey: 'sp_ms_oauth_guide_title',
    title: 'Connect with Microsoft (OAuth)',
    estimatedMinutes: 3,
    notes: [
      { key: 'sp_ms_oauth_note_works_for', text: 'Works for Microsoft 365 / Outlook.com / Office 365 accounts.' },
      { key: 'sp_ms_oauth_note_admin', text: 'If your IT admin restricts third-party apps, they may need to approve InteGolf for your tenant before this works.' },
    ],
    steps: [
      { titleKey: 'sp_ms_oauth_step_click_title', title: 'Click "Connect with Microsoft"', bodyKey: 'sp_ms_oauth_step_click_body', body: 'Below this guide, click the green "Connect with Microsoft" button. A new window opens to login.microsoftonline.com.' },
      { titleKey: 'sp_ms_oauth_step_signin_title', title: 'Sign in', bodyKey: 'sp_ms_oauth_step_signin_body', body: 'Use the Microsoft 365 account you want emails sent FROM.' },
      { titleKey: 'sp_ms_oauth_step_approve_title', title: 'Approve send permission', bodyKey: 'sp_ms_oauth_step_approve_body', body: 'Microsoft shows a consent screen listing "Send mail as you" and "Read your profile". Click Accept. You will be redirected back to InteGolf.' },
      { titleKey: 'sp_guide_step_done_title', title: 'Done', bodyKey: 'sp_ms_oauth_step_done_body', body: 'Your account appears in the integration list as Microsoft 365. Sent mail will appear in your Sent Items.' },
    ],
  },
  microsoft_app_password: {
    titleKey: 'sp_ms_apppw_guide_title',
    title: 'Use a Microsoft app password',
    estimatedMinutes: 6,
    notes: [
      { key: 'sp_ms_apppw_note_mfa', text: 'Requires MFA / 2-Step Verification enabled on your Microsoft account.' },
      { key: 'sp_ms_apppw_note_smtp', text: 'Some Microsoft 365 tenants disable basic SMTP auth entirely — if Save fails, ask your IT admin to enable SMTP AUTH for your mailbox or use OAuth instead.' },
    ],
    steps: [
      { titleKey: 'sp_ms_apppw_step_2step_title', title: 'Enable Two-Step Verification', bodyKey: 'sp_ms_apppw_step_2step_body', body: 'Go to your Microsoft Account → Security → Advanced security options → Two-step verification → Turn on.', link: { labelKey: 'sp_link_microsoft_security', label: 'Microsoft Security', url: 'https://account.microsoft.com/security' } },
      { titleKey: 'sp_ms_apppw_step_open_title', title: 'Open App Passwords', bodyKey: 'sp_ms_apppw_step_open_body', body: 'Same Advanced security options page → scroll to "App passwords" → "Create a new app password". (For business 365 accounts, this is in the Microsoft 365 admin center under Users → Multi-factor authentication.)', link: { labelKey: 'sp_link_app_passwords', label: 'App Passwords', url: 'https://account.microsoft.com/security/advanced' } },
      { titleKey: 'sp_ms_apppw_step_copy_title', title: 'Copy the generated password', bodyKey: 'sp_ms_apppw_step_copy_body', body: 'Microsoft shows a randomly-generated string. Copy it now.' },
      { titleKey: 'sp_guide_moneris_step_paste_title', title: 'Paste into InteGolf', bodyKey: 'sp_ms_apppw_step_paste_body', body: 'Your full email (e.g. you@yourcompany.com or you@outlook.com) into the email field. The app password into the App password field. Click Save integration.' },
    ],
  },
}

/**
 * Email integration page — Phase 2.
 *
 * Reseller picks SendGrid or AWS SES, enters creds, sends a test email.
 * Active integration is what the platform uses for outbound mail to
 * customers (booking confirmations, review invites, etc.) once those
 * features land in Phase 7+.
 *
 * Gmail OAuth + Microsoft Graph deferred — backend returns 501 for now.
 */
export default function EmailIntegration() {
  const { t } = useLanguage()
  const [params, setParams] = useSearchParams()
  const [integrations, setIntegrations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [testTo, setTestTo] = useState('')
  const [testingId, setTestingId] = useState(null)
  const [testResult, setTestResult] = useState(null)
  const [oauthBanner, setOauthBanner] = useState(null)

  // Detect return-from-OAuth-callback
  useEffect(() => {
    const provider = params.get('provider')
    const connected = params.get('connected')
    const oauthErr = params.get('error')
    const email = params.get('email')
    if (connected === '1') {
      setOauthBanner({ ok: true, message: email
        ? t('sp_oauth_connected_banner', '{provider} connected successfully as {email}.', { provider: labelFor(provider), email })
        : t('sp_oauth_connected_no_email', '{provider} connected successfully.', { provider: labelFor(provider) }) })
      params.delete('provider'); params.delete('connected'); params.delete('email'); setParams(params, { replace: true })
    } else if (oauthErr) {
      setOauthBanner({ ok: false, message: t('sp_oauth_connect_failed', '{provider} connect failed: {error}', { provider: labelFor(provider), error: oauthErr }) })
      params.delete('provider'); params.delete('error'); setParams(params, { replace: true })
    }
  }, [])

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/email-integrations')
      .then(r => setIntegrations(r.data?.integrations || []))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  async function activate(id) {
    try {
      await tournamentApi.post(`/portal/reseller/email-integrations/${id}/activate`)
      reload()
      setOauthBanner({ ok: true, message: t('sp_default_updated', 'Default integration updated — all customer emails will now route through it.') })
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }
  async function remove(id) {
    if (!confirm(t('sp_confirm_remove_integration', 'Remove this integration?'))) return
    try { await tournamentApi.delete(`/portal/reseller/email-integrations/${id}`); reload() }
    catch (err) { alert(err?.response?.data?.message || err.message) }
  }
  async function testSend(id) {
    if (!testTo.trim()) {
      setTestResult({ ok: false, message: t('sp_test_enter_recipient_first', 'Enter a recipient email in the "Test send to" box at the top first.') })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setTestingId(id); setTestResult(null)
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/email-integrations/${id}/test`, { to: testTo.trim() })
      setTestResult({ ok: true, message: t('sp_test_sent_success', 'Sent to {sentTo} via {provider}. Check the inbox.', { sentTo: data.sentTo, provider: data.provider }) })
    } catch (err) {
      setTestResult({ ok: false, message: err?.response?.data?.detail || err?.response?.data?.message || err.message })
    } finally { setTestingId(null) }
  }

  return (
    <ResellerShell title={t('sp_email_integration_title', 'Email integration')}>
      <p style={{ color: '#64748b', maxWidth: 720, marginTop: 0, marginBottom: 8, fontSize: 14 }}>
        {t('sp_email_intro_p1', 'Customer-facing emails (booking confirmations, payment reminders, review invites, etc.) send through your own email infrastructure — your domain, your reputation, your inbox for replies. Pick a provider, enter credentials, and send a test to verify.')}
      </p>
      <p style={{ color: '#64748b', maxWidth: 720, marginTop: 0, marginBottom: 24, fontSize: 13 }}>
        {t('sp_email_intro_p2_prefix', 'You can configure multiple integrations. The one marked')} <strong style={{ color: '#16a34a' }}>{t('sp_default_star', '★ Default')}</strong> {t('sp_email_intro_p2_suffix', 'is used to send every customer email — switch the default any time.')}
      </p>

      {error && <div style={errorBox}>{error}</div>}
      {oauthBanner && (
        <div style={oauthBanner.ok ? successBox : errorBox}>
          {oauthBanner.ok ? '✓ ' : '✗ '}{oauthBanner.message}
          <button onClick={() => setOauthBanner(null)} style={{ ...secondaryBtn, marginLeft: 12, padding: '4px 10px', fontSize: 12 }}>{t('dismiss', 'Dismiss')}</button>
        </div>
      )}

      {/* Test sender bar */}
      {integrations.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13, color: '#475569' }}>{t('sp_test_send_to_label', 'Test send to:')}</strong>
          <input
            type="email"
            placeholder="recipient@example.com"
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
            style={{ ...inp, flex: 1, minWidth: 240, maxWidth: 360 }}
          />
          <span style={{ fontSize: 12, color: testTo.trim() ? '#16a34a' : '#dc2626' }}>
            {testTo.trim()
              ? t('sp_test_send_hint_ready', '→ click "Test" on an integration below to send to {email}', { email: testTo })
              : t('sp_test_send_hint_empty', '↑ enter a recipient email above, then click "Test" on an integration below')}
          </span>
        </div>
      )}

      {testResult && (
        <div style={testResult.ok ? successBox : errorBox}>
          {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
          <button onClick={() => setTestResult(null)} style={{ ...secondaryBtn, marginLeft: 12, padding: '4px 10px', fontSize: 12 }}>{t('dismiss', 'Dismiss')}</button>
        </div>
      )}

      {loading
        ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div>
        : integrations.length === 0
          ? <div style={empty}>{t('sp_no_email_integrations', 'No email integrations yet. Add one below.')}</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {integrations.map(i => (
                <div key={i.ID_NO} style={{
                  background: '#fff',
                  border: i.IsActive ? '2px solid #16a34a' : '1px solid #e2e8f0',
                  borderRadius: 8, padding: 18,
                  display: 'flex', alignItems: 'center', gap: 16,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <strong style={{ fontSize: 15, color: '#0f172a' }}>{providerLabel(i.ProviderType)}</strong>
                      {i.IsActive
                        ? <Badge color="#16a34a">{t('sp_default_star', '★ Default')}</Badge>
                        : <Badge color="#64748b">{t('available', 'Available')}</Badge>}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                      {t('sp_from_label', 'From:')} <code>{i.Config?.fromEmail}</code>{i.Config?.fromName ? ` (${i.Config.fromName})` : ''}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontFamily: 'monospace' }}>
                      {credPreview(i.ProviderType, i.Config)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => testSend(i.ID_NO)}
                      disabled={testingId === i.ID_NO}
                      title={testTo.trim() ? t('sp_test_send_title_ready', 'Send a test email to {email}', { email: testTo }) : t('sp_test_send_title_empty', 'Enter a recipient email above first')}
                      style={{
                        ...secondaryBtn,
                        opacity: testingId === i.ID_NO ? 0.5 : 1,
                        cursor: testingId === i.ID_NO ? 'wait' : 'pointer',
                      }}
                    >
                      {testingId === i.ID_NO ? t('sp_sending_ellipsis', 'Sending…') : t('sp_mode_test', 'Test')}
                    </button>
                    {!i.IsActive && (
                      <button
                        onClick={() => activate(i.ID_NO)}
                        title={t('sp_set_default_title', 'Make this the default integration — every customer email will go through it')}
                        style={{ ...secondaryBtn, color: '#16a34a', borderColor: '#a7f3d0' }}
                      >
                        {t('sp_btn_set_as_default', '★ Set as default')}
                      </button>
                    )}
                    <button onClick={() => remove(i.ID_NO)} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove', 'Remove')}</button>
                  </div>
                </div>
              ))}
            </div>
          )
      }

      {!showAdd
        ? <button onClick={() => setShowAdd(true)} style={primaryBtn}>{t('sp_btn_add_email_integration', '+ Add email integration')}</button>
        : <AddIntegrationCard onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); reload() }} />
      }
    </ResellerShell>
  )
}

function AddIntegrationCard({ onClose, onCreated }) {
  const { t } = useLanguage()
  const [provider, setProvider] = useState('sendgrid')
  const [authMethod, setAuthMethod] = useState('oauth') // for gmail/microsoft: 'oauth' | 'app_password'
  const [showGuide, setShowGuide] = useState(false)
  const [fromEmail, setFromEmail] = useState('')
  const [fromName, setFromName] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [accessKeyId, setAccessKeyId] = useState('')
  const [secretAccessKey, setSecretAccessKey] = useState('')
  const [region, setRegion] = useState('us-east-1')
  const [appPassword, setAppPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setErr(null); setSubmitting(true)
    try {
      if ((provider === 'gmail' || provider === 'microsoft') && authMethod === 'oauth') {
        // OAuth flow — get consent URL + redirect
        const { data } = await tournamentApi.post(`/portal/reseller/email-integrations/connect/${provider}`,
          { fromName: fromName || null })
        window.location.assign(data.authUrl)
        return
      }
      const body = { provider, fromEmail, fromName }
      if (provider === 'sendgrid') body.apiKey = apiKey
      if (provider === 'aws_ses')  Object.assign(body, { accessKeyId, secretAccessKey, region })
      if ((provider === 'gmail' || provider === 'microsoft') && authMethod === 'app_password') {
        Object.assign(body, { authMethod: 'app_password', email: fromEmail, appPassword })
      }
      await tournamentApi.post('/portal/reseller/email-integrations', body)
      onCreated()
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally { setSubmitting(false) }
  }

  const isGmailOrMs = provider === 'gmail' || provider === 'microsoft'
  const isOauthMode = isGmailOrMs && authMethod === 'oauth'
  const isAppPwMode = isGmailOrMs && authMethod === 'app_password'

  // Resolve which guide to show based on provider + authMethod
  function guideKey() {
    if (provider === 'sendgrid') return 'sendgrid'
    if (provider === 'aws_ses')  return 'aws_ses'
    if (provider === 'gmail')    return authMethod === 'oauth' ? 'gmail_oauth' : 'gmail_app_password'
    if (provider === 'microsoft') return authMethod === 'oauth' ? 'microsoft_oauth' : 'microsoft_app_password'
    return null
  }
  const currentGuide = EMAIL_SETUP_GUIDES[guideKey()]

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 15 }}>{t('sp_new_email_integration', 'New email integration')}</h3>
      {err && <div style={errorBox}>{err}</div>}
      <form onSubmit={submit}>
        <Field label={t('sp_provider_row', 'Provider')}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ProviderTab label="SendGrid"      active={provider === 'sendgrid'}  onClick={() => setProvider('sendgrid')} />
            <ProviderTab label="AWS SES"       active={provider === 'aws_ses'}   onClick={() => setProvider('aws_ses')} />
            <ProviderTab label="Gmail"         active={provider === 'gmail'}     onClick={() => setProvider('gmail')} />
            <ProviderTab label="Microsoft 365" active={provider === 'microsoft'} onClick={() => setProvider('microsoft')} />
          </div>
        </Field>

        {currentGuide && (
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 16px', marginBottom: 14,
              background: '#eef2ff', color: '#4338ca',
              border: '1px solid #c7d2fe', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t('sp_btn_setup_guide', '📖 Setup Guide — How to get these credentials')}
          </button>
        )}

        {isGmailOrMs && (
          <Field label={t('sp_field_auth_method', 'Authentication method')}>
            <div style={{ display: 'flex', gap: 8 }}>
              <ProviderTab label={t('sp_authtab_oauth', 'Sign in (OAuth — recommended)')} active={authMethod === 'oauth'}        onClick={() => setAuthMethod('oauth')} />
              <ProviderTab label={t('sp_authtab_app_password', 'App password (SMTP)')}     active={authMethod === 'app_password'} onClick={() => setAuthMethod('app_password')} />
            </div>
          </Field>
        )}

        {isOauthMode ? (
          <>
            <Field label={t('sp_field_from_name_to_customers', 'From name shown to customers (optional)')}>
              <input type="text" style={inp} value={fromName} onChange={e => setFromName(e.target.value)} placeholder={t('sp_ph_your_company', 'Your Company')} />
            </Field>
            <div style={{ padding: 14, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 13, color: '#0c4a6e' }}>
              <strong>{t('sp_oauth_signin_label', '{provider} sign-in:', { provider: provider === 'gmail' ? 'Gmail' : 'Microsoft 365' })}</strong> {t('sp_oauth_signin_body', 'the next step opens {provider} to authorize InteGolf to send mail through your mailbox. We only request', { provider: provider === 'gmail' ? 'Google' : 'Microsoft' })} <strong>{t('sp_oauth_send_word', 'send')}</strong> {t('sp_oauth_signin_body_tail', 'permission — we cannot read your inbox. The "From" address customers see will be your authorized email account.')}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
              <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>
                {submitting ? t('sp_redirecting_ellipsis', 'Redirecting…') : t('sp_connect_with_provider', 'Connect with {provider}', { provider: provider === 'gmail' ? 'Google' : 'Microsoft' })}
              </button>
            </div>
          </>
        ) : isAppPwMode ? (
          <>
            <div style={{ display: 'flex', gap: 12 }}>
              <Field label={t('sp_field_full_email_address', 'Your full email address')} style={{ flex: 1 }}>
                <input type="email" required style={inp} value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder={provider === 'gmail' ? 'you@gmail.com or you@yourdomain.com' : 'you@yourcompany.com'} />
              </Field>
              <Field label={t('sp_field_from_name_optional', 'From name (optional)')} style={{ flex: 1 }}>
                <input type="text" style={inp} value={fromName} onChange={e => setFromName(e.target.value)} placeholder={t('sp_ph_your_company', 'Your Company')} />
              </Field>
            </div>
            <Field label={t('sp_field_app_password', 'App password')} hint={provider === 'gmail'
              ? t('sp_apppw_hint_gmail', 'Generate at myaccount.google.com/apppasswords (requires 2-step verification enabled). 16 characters, no spaces.')
              : t('sp_apppw_hint_microsoft', 'Generate in your Microsoft account security settings (requires MFA / 2FA enabled).')
            }>
              <input type="password" required style={{ ...inp, fontFamily: 'monospace' }} value={appPassword} onChange={e => setAppPassword(e.target.value.replace(/\s/g, ''))} placeholder="••••••••••••••••" />
            </Field>
            <div style={{ padding: 14, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#854d0e' }}>
              <strong>{t('sp_apppw_headsup_label', 'Heads-up:')}</strong> {t('sp_apppw_headsup_body', '2-step verification (2FA) must be enabled on your {provider} account before you can generate an app password. Sent mail will be visible in your Sent folder.', { provider: provider === 'gmail' ? 'Google' : 'Microsoft' })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
              <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? t('sp_saving', 'Saving…') : t('sp_btn_save_integration', 'Save integration')}</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12 }}>
              <Field label={t('sp_field_from_email', 'From email')} style={{ flex: 1 }}><input type="email" required style={inp} value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="noreply@yourbrand.com" /></Field>
              <Field label={t('sp_field_from_name_optional', 'From name (optional)')} style={{ flex: 1 }}><input type="text" style={inp} value={fromName} onChange={e => setFromName(e.target.value)} placeholder={t('sp_ph_your_company', 'Your Company')} /></Field>
            </div>
            {provider === 'sendgrid' && (
              <Field label={t('sp_field_sendgrid_api_key', 'SendGrid API key')} hint={t('sp_hint_sendgrid_api_key', 'Starts with SG.* — needs Mail Send permission.')}>
                <input type="text" required style={{ ...inp, fontFamily: 'monospace' }} value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="SG.xxxxxxxx" />
              </Field>
            )}
            {provider === 'aws_ses' && (
              <>
                <Field label={t('sp_field_aws_access_key_id', 'AWS Access Key ID')}><input type="text" required style={{ ...inp, fontFamily: 'monospace' }} value={accessKeyId} onChange={e => setAccessKeyId(e.target.value)} placeholder="AKIAxxxxxxxx" /></Field>
                <Field label={t('sp_field_aws_secret_access_key', 'AWS Secret Access Key')}><input type="password" required style={{ ...inp, fontFamily: 'monospace' }} value={secretAccessKey} onChange={e => setSecretAccessKey(e.target.value)} placeholder="••••••••" /></Field>
                <Field label={t('sp_field_aws_region', 'AWS Region')} hint={t('sp_hint_aws_region', 'The region where your SES verified identity lives.')}><input type="text" required style={inp} value={region} onChange={e => setRegion(e.target.value)} placeholder="us-east-1" /></Field>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
              <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? t('sp_saving', 'Saving…') : t('sp_btn_save_integration', 'Save integration')}</button>
            </div>
          </>
        )}
      </form>
      {showGuide && currentGuide && <EmailSetupGuideModal guide={currentGuide} onClose={() => setShowGuide(false)} />}
    </div>
  )
}

function labelFor(p) {
  return ({ sendgrid: 'SendGrid', aws_ses: 'AWS SES', gmail: 'Gmail', microsoft: 'Microsoft 365' })[p] || p || 'Provider'
}

function EmailSetupGuideModal({ guide, onClose }) {
  const { t } = useLanguage()
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, maxWidth: 640, width: '100%',
        maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '1.6rem' }}>📖</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>{t(guide.titleKey, guide.title)}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                {t('sp_guide_minutes_steps', '⏱ ~{minutes} minutes · {count} steps', { minutes: guide.estimatedMinutes, count: guide.steps.length })}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9ca3af', padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {/* Notes */}
        {guide.notes?.length > 0 && (
          <div style={{ margin: '16px 24px 0', padding: '12px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.85rem', marginBottom: 6 }}>{t('sp_before_you_start', 'Before you start')}</div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.82rem', color: '#78350f', lineHeight: 1.55 }}>
              {guide.notes.map((n, i) => <li key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>{t(n.key, n.text)}</li>)}
            </ul>
          </div>
        )}

        {/* Steps */}
        <ol style={{ padding: '20px 24px', margin: 0, listStyle: 'none' }}>
          {guide.steps.map((step, i) => (
            <li key={i} style={{
              display: 'flex', gap: 14, paddingBottom: 18,
              borderLeft: i < guide.steps.length - 1 ? '2px solid #e5e7eb' : 'none',
              marginLeft: 14, paddingLeft: 18, position: 'relative',
            }}>
              <div style={{
                position: 'absolute', left: -16, top: 0,
                width: 30, height: 30, borderRadius: '50%',
                background: '#00D19F', color: '#0f172a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '0.85rem',
              }}>{i + 1}</div>
              <div style={{ flex: 1, marginLeft: 18 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: 4 }}>{t(step.titleKey, step.title)}</div>
                <div style={{ fontSize: '0.86rem', color: '#374151', lineHeight: 1.55 }}>{t(step.bodyKey, step.body)}</div>
                {step.link && (
                  <a href={step.link.url} target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    marginTop: 8, padding: '6px 12px', borderRadius: 6,
                    background: '#eef2ff', color: '#4338ca',
                    fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
                  }}>🔗 {t(step.link.labelKey, step.link.label)} ↗</a>
                )}
              </div>
            </li>
          ))}
        </ol>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', textAlign: 'right', position: 'sticky', bottom: 0, background: '#fff' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {t('sp_got_it', 'Got it')}
          </button>
        </div>
      </div>
    </div>
  )
}

function ProviderTab({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '10px 16px',
      background: active ? '#0f172a' : '#fff', color: active ? '#fff' : '#475569',
      border: '1px solid ' + (active ? '#0f172a' : '#e2e8f0'), borderRadius: 6,
      fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
    }}>{label}</button>
  )
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}
function Badge({ color, children }) {
  return <span style={{ padding: '2px 8px', background: color + '15', color, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{children}</span>
}
function providerLabel(p) {
  return ({ sendgrid: 'SendGrid', aws_ses: 'AWS SES', gmail: 'Gmail', microsoft: 'Microsoft 365' })[p] || p
}
function credPreview(provider, cfg) {
  if (!cfg) return ''
  if (provider === 'sendgrid') return `apiKey: ${cfg.apiKey || '—'}`
  if (provider === 'aws_ses')  return `accessKeyId: ${cfg.accessKeyId || '—'} · region: ${cfg.region || '—'}`
  return ''
}

const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const successBox = { padding: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16 }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
